import { useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useRosterGetAll, useCrewGetAll, useTripLegsByCrewAndDateRange } from './index'
import { DutyStatus } from '../../../../api/src/shared'

export interface TripLegData {
  id: string
  departureAirportIcao: string
  arrivalAirportIcao: string
  departureDateTime: string
  arrivalDateTime: string
  aircraft?: { registration: string; tailNumber?: string | null } | null
  trip: {
    id: string
  }
}

export interface TripCrewAssignment {
  id: string
  tripLeg: TripLegData
}

export interface RosterEntryData {
  id: string
  date: Dayjs
  crewId: string
  dutyStatus: DutyStatus
  position?: string
  note?: string | null
  tripCrewAssignments?: TripCrewAssignment[]
}

export interface CrewMemberData {
  id: string
  name: string
  firstName?: string | null
  lastName?: string | null
  avatarColor?: string | null
  imageUrl?: string | null
  crewRole?: string
}

export interface UseRosterDataParams {
  startDate: string // YYYY-MM-DD
  endDate: string // YYYY-MM-DD
  dutyStatus?: DutyStatus
  aircraftIds?: string[]
}

export interface UseRosterDataResult {
  // Raw data
  rosterEntries: RosterEntryData[]
  crewMembers: CrewMemberData[]
  tripLegs: any[]

  // Computed maps
  crewStatusMap: Record<string, Record<string, { status: DutyStatus; entryId: string }>>
  crewTripLegMap: Record<string, Record<string, TripLegData[]>>
  crewFlightMap: Record<string, Record<string, string>> // crewId -> date -> tripId

  // Loading states
  isLoading: boolean
  loadingRoster: boolean
  loadingCrew: boolean
}

export function useRosterData({
  startDate,
  endDate,
  dutyStatus,
  aircraftIds,
}: UseRosterDataParams): UseRosterDataResult {
  // Fetch roster entries
  const {
    data: rawRosterEntries = [],
    isLoading: loadingRoster,
  } = useRosterGetAll({
    startDate,
    endDate,
    aircraftIds: aircraftIds && aircraftIds.length > 0 ? aircraftIds : undefined,
  })

  // Fetch crew members. Crew excluded from the schedule never appear on the
  // roster, so drop them at the source.
  const { data: rawCrewMembers = [], isLoading: loadingCrew } = useCrewGetAll()
  const schedulableCrew = useMemo(
    () => rawCrewMembers.filter((c: any) => c.includeInSchedule !== false),
    [rawCrewMembers]
  )

  // Get crew IDs for trip leg query
  const crewIds = useMemo(() => schedulableCrew.map((c: any) => c.id), [schedulableCrew])

  // Fetch trip legs for all crew members in the date range
  const { data: tripLegs = [] } = useTripLegsByCrewAndDateRange({
    crewIds,
    startDate: new Date(startDate),
    endDate: new Date(endDate),
  })

  // Transform roster entries to include Dayjs date
  // Apply dutyStatus filter if provided
  const rosterEntries = useMemo(() => {
    return rawRosterEntries
      .filter((entry: any) => !dutyStatus || entry.dutyStatus === dutyStatus)
      .map((entry: any): RosterEntryData => ({
        id: entry.id,
        date: dayjs(entry.date),
        crewId: entry.crewId,
        dutyStatus: entry.dutyStatus,
        position: entry.position,
        note: entry.note,
      }))
  }, [rawRosterEntries, dutyStatus])

  // Transform crew members
  const crewMembers = useMemo(() => {
    return schedulableCrew.map((crew: any): CrewMemberData => ({
      id: crew.id,
      name: crew.name,
      firstName: crew.firstName,
      lastName: crew.lastName,
      avatarColor: crew.avatarColor,
      imageUrl: crew.imageUrl,
      crewRole: crew.crewRole,
    }))
  }, [schedulableCrew])

  // Build crew status map: crewId -> date -> { status, entryId }
  const crewStatusMap = useMemo(() => {
    const map: Record<string, Record<string, { status: DutyStatus; entryId: string }>> = {}

    rosterEntries.forEach((entry) => {
      if (!map[entry.crewId]) {
        map[entry.crewId] = {}
      }

      const dateKey = entry.date.format('YYYY-MM-DD')
      const existing = map[entry.crewId][dateKey]

      // Use priority if multiple entries exist for same day
      const getPriority = (status: DutyStatus): number => {
        switch (status) {
          case DutyStatus.OnDuty: return 5
          case DutyStatus.Training: return 4
          case DutyStatus.Standby: return 3
          case DutyStatus.OffDuty: return 1
          default: return 0
        }
      }

      if (!existing || getPriority(entry.dutyStatus) > getPriority(existing.status)) {
        map[entry.crewId][dateKey] = {
          status: entry.dutyStatus,
          entryId: entry.id,
        }
      }
    })

    return map
  }, [rosterEntries])

  // Build trip leg map: crewId -> date -> tripLeg data (array for multiple flights per day)
  const crewTripLegMap = useMemo(() => {
    const map: Record<string, Record<string, TripLegData[]>> = {}

    tripLegs.forEach((leg: any) => {
      const legStartDate = dayjs(leg.departureDateTime).startOf('day')
      const legEndDate = dayjs(leg.arrivalDateTime).startOf('day')
      let currentDate = legStartDate

      while (currentDate.isBefore(legEndDate) || currentDate.isSame(legEndDate, 'day')) {
        const dateKey = currentDate.format('YYYY-MM-DD')

        leg.crew.forEach((crewMember: { id: string }) => {
          if (!map[crewMember.id]) {
            map[crewMember.id] = {}
          }
          if (!map[crewMember.id][dateKey]) {
            map[crewMember.id][dateKey] = []
          }
          map[crewMember.id][dateKey].push({
            id: leg.id,
            departureAirportIcao: leg.departureAirportIcao,
            arrivalAirportIcao: leg.arrivalAirportIcao,
            departureDateTime: leg.departureDateTime,
            arrivalDateTime: leg.arrivalDateTime,
            aircraft: leg.aircraft,
            trip: leg.trip,
          })
        })

        currentDate = currentDate.add(1, 'day')
      }
    })

    return map
  }, [tripLegs])

  // Build flight map: crewId -> date -> tripId (only on departure date)
  const crewFlightMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {}

    tripLegs.forEach((leg: any) => {
      const dateKey = dayjs(leg.departureDateTime).format('YYYY-MM-DD')

      leg.crew.forEach((crewMember: { id: string }) => {
        if (!map[crewMember.id]) {
          map[crewMember.id] = {}
        }
        map[crewMember.id][dateKey] = leg.trip.id
      })
    })

    return map
  }, [tripLegs])

  return {
    rosterEntries,
    crewMembers,
    tripLegs,
    crewStatusMap,
    crewTripLegMap,
    crewFlightMap,
    isLoading: loadingRoster || loadingCrew,
    loadingRoster,
    loadingCrew,
  }
}
